/**
 * @module Phone
 * @description Phone Page
 */
import Head from "next/head";

// Template
import Template from "@/templates/Default";

// Blocks
import HeroAddressCheck from "@/blocks/Heroes/HeroAddressCheck";
import SplitSectionCopy from "@/blocks/SplitSection/SplitSectionCopy";
import ColumnContent from "@/blocks/ColumnContent";
import BannerCTA from "@/blocks/Banners/BannerCTA";
import FAQs from "@/blocks/FAQS";

// Schemas
import { UnlimitedDigitalVoiceProductSchema } from "@/js/utils/schema";

/**
 * @function Page
 * @description Page
 */
const Page = () => (
  <>
    <Head>
      <title>Frontier Phone Service: Reliable Home Landline & VoIP Plans</title>
      <meta
        name="description"
        content="Get Frontier phone services with home phone packages that offer unlimited nationwide calling, voicemail & more."
      />
      <link rel="canonical" href="https://frontier.com/shop/phone" />
    </Head>

    <Template
      title="Frontier Phone and VoIP Services Keep You Connected"
      hero={
        <HeroAddressCheck
          headline={
            <>
              <span className="u-color-blue">Connect</span> Your World
            </>
          }
          subHeadline={
            <>
              Add Frontier<sup>&reg;</sup> Home Phone to your internet plan to
              easily keep up with friends and family near and far.&nbsp;
              <span data-legal="frontier-voice-line" />
            </>
          }
          background="woman-coffee-phone"
          useSelectAreasLegal={
            <>
              Frontier<sup>&reg;</sup> Fiber Internet available in
              select&nbsp;areas.
            </>
          }
        />
      }
      primary={
        <>
          <SplitSectionCopy
            title="Stay connected with Frontier Home Phone"
            content={[
              {
                copy: "Frontier Home Phone services offer reliable call quality day and night. And whether you’re calling into work meetings from the comfort of your home or you’re looking to save minutes on your cell phone plan, adding a home phone can help bring you and your family peace of mind.",
              },
            ]}
            image={{
              url: "/sections/split-section/mother-daughter-using-phone.jpg",
              blurUrl:
                "/sections/split-section/mother-daughter-using-phone.jpg",
              alt: "Mother and daughter using home phone",
            }}
            subtitle={false}
          />

          <ColumnContent
            headline="Our Home Phone plan includes:"
            items={[
              {
                icon: "phone-outline",
                headline: "Reliable call quality",
                content:
                  "Get reliable call quality day and night, and have great conversations with your friends and family.",
              },
              {
                icon: "global-wifi",
                headline: "Unlimited long-distance",
                content:
                  "Make the distances feel smaller with unlimited nationwide & long-distance calling.",
              },
              {
                icon: "fiber",
                headline: "Full features",
                content:
                  "Get the most out of your plan with helpful call management features like caller ID, Call Waiting, and Voicemail.",
              },
            ]}
          />

          <BannerCTA
            theme="blue"
            copy="Ready to see internet plans available in your neighborhood?"
            usePhone
          />

          <FAQs
            accordionData={[
              {
                title: "What is included with Frontier home phone plans?",
                content:
                  "Depending on which plan you select, you’ll get features such as unlimited local and long-distance calling, voicemail and caller ID.",
              },
              {
                title:
                  "Do I need Frontier Internet to get Frontier Home Phone?",
                content:
                  "It depends on the phone service available at your address. The best way to find out is to call one of our helpful representatives or enter your address in our convenient online cart.",
              },
              {
                title: "Does a Frontier Home Phone plan include a phone?",
                content:
                  "No, a phone is not included with our Home Phone plans. BYOP (Bring Your Own Phone).",
              },
            ]}
          />
        </>
      }
    ></Template>
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: `${UnlimitedDigitalVoiceProductSchema()}`,
      }}
    />
  </>
);

export default Page;
